import React, { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import useCustomTheme from '../../hooks/theme-hook';
import { SwitchElement } from '../../models/helpers/platformImportHelper';

export interface CustomSwitchProps {
  onPress?(e: boolean): void;
  isEnabled?: boolean;
  onColor?: string;
  offColor?: string;
  disabled?: boolean;
  accessibilityLabel?: string;
}

const CustomSwitch = React.memo((props: CustomSwitchProps) => {
  const theme = useCustomTheme();
  const switchRef = useRef<any>(null);
  const SwitchComponent = SwitchElement as any;
  const isEnabled = props.isEnabled ?? false;
  const onColor = props.onColor ?? theme.primary?.toString();
  const offColor = props.offColor ?? theme.borderColor?.toString();

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    const target = switchRef.current;
    const thumbElement = target?.previousSibling as HTMLElement | undefined;
    const trackElement = thumbElement?.previousSibling as HTMLElement | undefined;
    const containerElement = trackElement?.parentElement as HTMLElement | undefined;

    if (containerElement) {
      containerElement.style.paddingLeft = '2px';
      containerElement.style.paddingRight = '2px';
    }

    if (trackElement) {
      trackElement.style.height = '24px';
      trackElement.style.borderRadius = '12px';
    }

    if (thumbElement) {
      thumbElement.style.backgroundColor = 'white';
    }
  }, [isEnabled]);

  if (Platform.OS === 'web') {
    return (
      <SwitchComponent
        ref={switchRef}
        accessibilityLabel={props.accessibilityLabel}
        trackColor={{ false: offColor, true: onColor }}
        thumbColor="white"
        value={isEnabled}
        disabled={props.disabled}
        onValueChange={props.onPress}
      />
    );
  }

  return (
    <SwitchComponent
      accessibilityLabel={props.accessibilityLabel}
      isOn={isEnabled}
      onColor={onColor}
      offColor={offColor}
      size="medium"
      disabled={props.disabled}
      onToggle={props.onPress}
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.isEnabled === nextProps.isEnabled &&
    prevProps.onColor === nextProps.onColor &&
    prevProps.offColor === nextProps.offColor &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.accessibilityLabel === nextProps.accessibilityLabel &&
    prevProps.onPress === nextProps.onPress
  );
});

export default CustomSwitch;
